import { ModalAction } from '../ModalAction'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useApp } from '@/core/state/appStore'
import { getParticipant, updateParticipantStammdaten, type ParticipantStammdaten } from '@/core/data/coachings'
import { isStammdatenComplete, missingStammdaten, type StammdatenContact } from '@/core/lib/stammdatenComplete'
import { completeStammdatenAsStaff } from '@/core/lib/stammdatenStaffComplete'
import { createParticipantLink } from '@/core/api/portal'
import { captureWriteIdentity, isWriteIdentityCurrent } from '@/core/api/http'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import { toastBlocked } from '@/core/lib/toast'
import { FormRenderer } from '@/core/components/form/FormRenderer'
import { TextField } from '@/core/components/form/TextField'
import { updateFormField, initialFormValues, type FormValues } from '@/core/components/form/formModel'
import { stammdatenForm } from '@/core/templates'
import { STAMMDATEN_INPUT_RULES } from '@/core/components/teilnehmer/StammdatenTab'
import type { FieldError, FieldErrors } from '@/core/lib/fieldError'
import { useAutosave } from '@/core/hooks/useAutosave'
import { ModalHeader } from '@/core/components/modal/ModalHeader'
import { Callout, LoadingState, ScrollBar, toastError, toastSuccess } from 'admination-react'

const STAMMDATEN_FIELDS = new Set<string>(
  Object.values(stammdatenForm.sections).flatMap(({ fields }) => fields.map((field) => field.id)),
)

const MISSING_FIELD_PATHS: Record<string, string> = {
  geburtsdatum: 'Person.Geburtsdatum',
  phone: 'Kontakt.Telefon',
  strasse: 'Kontakt.Straße',
  hausnummer: 'Kontakt.Hausnummer',
  plz: 'Kontakt.PLZ',
  ort: 'Kontakt.Ort',
}

function buildStammdatenErrors(missing: string[]): FieldErrors {
  const errors = new Map<string, FieldError>()
  for (const field of missing) {
    const path = MISSING_FIELD_PATHS[field]
    if (path) errors.set(path, { kind: 'missing' })
  }
  return errors
}

function str(section: Record<string, unknown> | undefined, key: string): string {
  const v = section?.[key]
  return typeof v === 'string' ? v : ''
}

function patchFromValues(values: FormValues): ParticipantStammdaten {
  const person = values['Person']
  const kontakt = values['Kontakt']
  return {
    geburtsdatum: str(person, 'Geburtsdatum'),
    strasse: str(kontakt, 'Straße'),
    hausnummer: str(kontakt, 'Hausnummer'),
    plz: str(kontakt, 'PLZ'),
    ort: str(kontakt, 'Ort'),
    telefon: str(kontakt, 'Telefon'),
  }
}

function contactFromValues(values: FormValues): StammdatenContact {
  const p = patchFromValues(values)
  return {
    geburtsdatum: p.geburtsdatum,
    phone: p.telefon,
    strasse: p.strasse,
    hausnummer: p.hausnummer,
    plz: p.plz,
    ort: p.ort,
  }
}

type SaveOutcome = { complete: boolean }

export function AvgsStammdaten({ coachingId }: { coachingId: string | null }) {
  const { dispatch, reloadDashboard } = useApp()
  const scrollerRef = useRef<HTMLElement>(null)
  const alive = useRef(true)
  useEffect(() => { alive.current = true; return () => { alive.current = false } }, [])
  const [reminding, setReminding] = useState(false)
  const [advancing, setAdvancing] = useState(false)
  const [complete, setComplete] = useState<boolean | undefined>(undefined)
  const [values, setValues] = useState<FormValues | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [kundennummer, setKundennummer] = useState<string | null>(null)
  const [showErrors, setShowErrors] = useState(false)
  const lastReportedComplete = useRef<boolean | undefined>(undefined)

  const loadParticipant = useCallback(() => {
    if (!coachingId) {
      setComplete(true)
      return
    }
    getParticipant(coachingId)
      .then((p) => {
        const isComplete = !!p && isStammdatenComplete(p)
        setComplete(isComplete)
        lastReportedComplete.current = isComplete
        setKundennummer(p?.kundennummer ?? null)
        const v = initialFormValues(stammdatenForm, {
          Person: {
            Geburtsdatum: p?.geburtsdatum ?? '',
          },
          Kontakt: {
            Telefon: p?.phone ?? '',
            Straße: p?.strasse ?? '',
            Hausnummer: p?.hausnummer ?? '',
            PLZ: p?.plz ?? '',
            Ort: p?.ort ?? '',
          },
        })
        setValues(v)
      })
      .catch((e) => {
        console.error('Stammdaten-Prüfung fehlgeschlagen:', e)
        setLoadError(true)
      })
  }, [coachingId])

  useEffect(() => {
    setComplete(undefined)
    setValues(null)
    setLoadError(false)
    setKundennummer(null)
    setShowErrors(false)
    loadParticipant()
  }, [loadParticipant])

  const setField = useCallback((path: string, v: unknown) => {
    setValues((prev) => updateFormField(prev, path, v))
  }, [])

  const save = useCallback(
    (v: FormValues | null, baseline: FormValues | null | undefined, assertCurrent: () => void): Promise<SaveOutcome> => {
      if (!v || !coachingId) return Promise.reject(new Error('Keine Stammdaten geladen.'))
      const current = patchFromValues(v)
      const previous = baseline ? patchFromValues(baseline) : undefined
      const changed = Object.fromEntries(Object.entries(current).filter(([key, value]) => previous?.[key as keyof ParticipantStammdaten] !== value))
      const run: Promise<SaveOutcome> = (Object.keys(changed).length ? updateParticipantStammdaten(coachingId, changed) : Promise.resolve())
        .then(() => { assertCurrent(); return completeStammdatenAsStaff(coachingId) })
        .then((result): SaveOutcome => {
          setComplete(result.complete)
          if (lastReportedComplete.current !== result.complete) {
            lastReportedComplete.current = result.complete
            reloadDashboard()
          }
          return { complete: result.complete }
        })
        .catch((e) => {
          console.error('Teilnehmer speichern fehlgeschlagen:', e)
          throw e
        })
      return run
    },
    [coachingId, reloadDashboard],
  )

  const { flush, status } = useAutosave(values, values !== null && !!coachingId, save, 250, { form: "AvgsStammdaten", record: coachingId ?? undefined, recover: setValues, label: 'Stammdaten' })

  const missingFields = useMemo(() => (values ? missingStammdaten(contactFromValues(values)) : []), [values])
  const stammdatenErrors = useMemo(() => buildStammdatenErrors(missingFields), [missingFields])

  function sendReminder() {
    if (!coachingId || reminding) return
    const identity = captureWriteIdentity()
    setReminding(true)
    createParticipantLink(coachingId, 'stammdaten', true, identity)
      .then((link) => {
        if (!isWriteIdentityCurrent(identity)) return
        toastSuccess(`Erinnerung an ${link.sent_to} gesendet.`)
      })
      .catch((e) => {
        if (!isWriteIdentityCurrent(identity)) return
        console.error('Erinnerung konnte nicht gesendet werden:', e)
        toastError(friendlyErrorMessage(e, 'Erinnerung konnte nicht gesendet werden.'))
      })
      .finally(() => { if (isWriteIdentityCurrent(identity)) setReminding(false) })
  }

  function block() {
    toastBlocked('Stammdaten noch nicht vollständig.')
    setShowErrors(true)
  }

  async function advance() {
    if (advancing) return
    if (!values || !isStammdatenComplete(contactFromValues(values))) {
      block()
      return
    }
    const pending = flush()
    if (!pending) {
      if (complete === true) dispatch({ type: "CHANGE_TAB", tab: 'upload' })
      else block()
      return
    }
    setAdvancing(true)
    try {
      const outcome = await pending
      if (!alive.current) return
      if (outcome.complete) dispatch({ type: "CHANGE_TAB", tab: 'upload' })
      else block()
    } catch {
      return
    } finally {
      setAdvancing(false)
    }
  }

  return (
    <>
      <ModalHeader>
        <div className="pill-cluster">
          {coachingId && complete !== undefined && (
            <ModalAction
              icon="i-send"
              title="Erinnerung senden"
              onClick={sendReminder}
              loading={reminding}
            />
          )}
          <ModalAction
            icon="i-arrow-right"
            primaryAction
            title="Weiter"
            loading={advancing}
            disabled={complete === undefined || advancing}
            onClick={() => void advance()}
          />
        </div>
      </ModalHeader>
      {!values && <main className="form">
        {loadError ? (
          <Callout tone="error" role="alert">Die Stammdaten konnten nicht geladen werden. Schließen Sie das Fenster und öffnen Sie es erneut.</Callout>
        ) : (
          <LoadingState>Stammdaten werden geladen …</LoadingState>
        )}
      </main>}
      {values && (
        <>
          <main className="form" data-scroll-host ref={scrollerRef}>
            {kundennummer && (
              <div className="section">
                <h3 className="section-header">Teilnehmer</h3>
                <div className="section-body">
                  <TextField label="Kundennummer" value={kundennummer} onChange={() => {}} readOnly />
                </div>
              </div>
            )}
            <FormRenderer
                definition={stammdatenForm}
                values={values}
                onChange={setField}
                errors={showErrors || status === 'error' ? stammdatenErrors : undefined}
                field={(field) => ({
                  required: STAMMDATEN_FIELDS?.has(field.id),
                  missing: STAMMDATEN_FIELDS?.has(field.id) ? 'mark' : undefined,
                  ...STAMMDATEN_INPUT_RULES?.get(field.id),
                })}
              />
          </main>
          <ScrollBar scrollerRef={scrollerRef} />
        </>
      )}
    </>
  )
}
